import React from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import { Truck } from "../../../types";
import { colors, getStatusColor } from "../../../utils/colors";
import { formatDateTime } from "../../../utils/dateUtils";
import { ActionMenu } from "../../ui/ActionMenu";

interface VehicleTableRowProps {
  registration: string;
  trucks: Truck[];
  token?: string;
  onDetailsClick: (registration: string) => void;
  onDirectionChange: (truck: Truck, direction: "in" | "out") => void;
  onLicensePlateChange?: (truck: Truck, newLicensePlate: string) => void;
  onDelete: (truck: Truck) => void;
  onUpdateTruck: (truck: Truck) => void;
  groupedTrucks: Record<string, Truck[]>;
}

const VehicleTableRow: React.FC<VehicleTableRowProps> = ({
  registration,
  trucks,
  token,
  onDetailsClick,
  onDirectionChange,
  onLicensePlateChange,
  onDelete,
  onUpdateTruck,
  groupedTrucks,
}) => {
  const getLatestTruck = (trucks: Truck[]) => {
    return trucks && trucks.length > 0 ? trucks[0] : null;
  };

  const getAvailabilityStatus = (trucks: Truck[]) => {
    const latestTruck = getLatestTruck(trucks);
    if (!latestTruck) return "Unknown";
    const lastDirection = latestTruck.direction?.toLowerCase() || "";

    if (lastDirection === "in") {
      return "Unavailable";
    } else if (lastDirection === "out") {
      return "Available";
    } else {
      return "Unknown";
    }
  };

  const latestTruck = getLatestTruck(trucks);
  const availabilityStatus = getAvailabilityStatus(trucks);
  const availabilityColor = getStatusColor(availabilityStatus);

  const cellStyle = { fontFamily: "SigmaSans", width: "14.29%" };

  return (
    <TableRow
      sx={{
        backgroundColor: colors.cardBackground,
        "&:nth-of-type(even)": {
          backgroundColor: colors.background,
        },
        "&:nth-of-type(odd)": {
          backgroundColor: colors.cardBackground,
        },
      }}
    >
      <TableCell component="th" scope="row" style={cellStyle}>
        {registration}
      </TableCell>
      <TableCell align="left" style={cellStyle}>
        {latestTruck?.intime ? formatDateTime(latestTruck.intime) : "N/A"}
      </TableCell>
      <TableCell align="left" style={cellStyle}>
        {latestTruck?.yard || "N/A"}
      </TableCell>
      <TableCell align="center" style={{ width: "16.67%" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 2,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: availabilityColor,
            color: "white",
            fontSize: "0.75rem",
            fontFamily: "SigmaSans",
            fontWeight: "bold",
          }}
        >
          {availabilityStatus}
        </Box>
      </TableCell>
      <TableCell align="center" style={{ width: "16.67%" }}>
        <Box
          component="span"
          onClick={() => onDetailsClick(registration)}
          sx={{
            color: colors.volvoBlue,
            textDecoration: "underline",
            cursor: "pointer",
            fontFamily: "SigmaSans",
            fontSize: "0.875rem",
            "&:hover": {
              color: colors.complementaryBlue,
            },
          }}
        >
          View Details
        </Box>
      </TableCell>
      <TableCell
        align="center"
        style={{ width: "16.67%", color: colors.error, fontWeight: "bold" }}
      >
        {latestTruck?.has_adr ? "ADR" : ""}
      </TableCell>
      <TableCell align="right" style={{ width: "16.67%" }}>
        <ActionMenu
          truck={trucks}
          token={token}
          onDirectionChange={onDirectionChange}
          onLicensePlateChange={(truck, newLicensePlate) => {
            if (onLicensePlateChange) {
              onLicensePlateChange(truck, newLicensePlate);
            } else {
              const updatedTruck = {
                ...truck,
                registration: newLicensePlate,
              };
              onUpdateTruck(updatedTruck);
            }
          }}
          onDelete={onDelete}
          groupedTrucks={groupedTrucks}
          context="main-table"
        />
      </TableCell>
    </TableRow>
  );
};

export default VehicleTableRow;
